import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import useAlert from './useAlert';

// Contrato canônico do payload e das regras de cancelamento — ver bloco de
// documentação em server/controllers/pedidosController.js (CONTRATO CANÔNICO
// — PedidoPayload). cancelarPedido (abaixo) já usa o endpoint dedicado
// correto — nunca trocar por updatePedido com { status: 'cancelado' }.

// ======================================================
// 🎯 QUERY KEYS
// ======================================================
export const pedidoKeys = {
  all: ['pedidos'],
  lists: () => [...pedidoKeys.all, 'list'],
  list: (filters) => [...pedidoKeys.lists(), { filters }],
  details: () => [...pedidoKeys.all, 'detail'],
  detail: (id) => [...pedidoKeys.details(), id],
  statistics: () => [...pedidoKeys.all, 'statistics'],
  cliente: (clienteId) => [...pedidoKeys.all, 'cliente', clienteId],
  pendentes: () => [...pedidoKeys.all, 'pendentes'],
};

// ======================================================
// 🎯 API FUNCTIONS
// ======================================================

// 🔹 GET - Listar pedidos com filtros
const fetchPedidos = async (params = {}) => {
  const { data } = await apiClient.get('/pedidos', { params });
  return data;
};

// 🔹 GET - Buscar pedido por ID
const fetchPedido = async (id) => {
  const { data } = await apiClient.get(`/pedidos/${id}`);
  return data;
};

// 🔹 GET - Buscar pedido por número
const fetchPedidoPorNumero = async (numero) => {
  const { data } = await apiClient.get(`/pedidos/numero/${numero}`);
  return data;
};

// 🔹 GET - Pedidos por cliente
const fetchPedidosPorCliente = async (clienteId, params = {}) => {
  const { data } = await apiClient.get(`/pedidos/cliente/${clienteId}`, { params });
  return data;
};

// 🔹 GET - Estatísticas de pedidos
const fetchEstatisticasPedidos = async (params = {}) => {
  const { data } = await apiClient.get('/pedidos/estatisticas/geral', { params });
  return data;
};

// 🔹 GET - Pedidos com pagamentos pendentes
const fetchPedidosPagamentosPendentes = async () => {
  const { data } = await apiClient.get('/pedidos/filtro/pagamentos-pendentes');
  return data;
};

// 🔹 GET - Fluxo completo do pedido
const fetchFluxoCompletoPedido = async (id) => {
  const { data } = await apiClient.get(`/pedidos/${id}/fluxo-completo`);
  return data;
};

// 🔹 POST - Criar pedido
const createPedido = async (pedidoData) => {
  const { data } = await apiClient.post('/pedidos', pedidoData);
  return data;
};

// 🔹 PUT - Atualizar pedido
const updatePedido = async ({ id, pedidoData }) => {
  const { data } = await apiClient.put(`/pedidos/${id}`, pedidoData);
  return data;
};

// 🔹 DELETE - Excluir pedido
const deletePedido = async (id) => {
  const { data } = await apiClient.delete(`/pedidos/${id}`);
  return data;
};

// 🔹 PUT - Cancelar pedido
const cancelarPedido = async ({ id, motivo }) => {
  const { data } = await apiClient.put(`/pedidos/${id}/cancelar`, { motivo });
  return data;
};

// 🔹 POST - Processar pedido
const processarPedido = async (id) => {
  const { data } = await apiClient.post(`/pedidos/${id}/processar`);
  return data;
};

// 🔹 POST - Criar venda a partir do pedido
const criarVendaPedido = async (id) => {
  const { data } = await apiClient.post(`/pedidos/${id}/criar-venda`);
  return data;
};

// 🔹 POST - Adicionar pagamento ao pedido
const adicionarPagamento = async ({ pedidoId, pagamentoData }) => {
  const { data } = await apiClient.post(`/pedidos/${pedidoId}/pagamentos`, pagamentoData);
  return data;
};

// ======================================================
// 🎯 REACT QUERY HOOKS
// ======================================================

// 🔹 HOOK: Listar pedidos
export const usePedidos = (params = {}) => {
  return useQuery({
    queryKey: pedidoKeys.list(params),
    queryFn: () => fetchPedidos(params),
    staleTime: 5 * 60 * 1000, // 5 minutos
    keepPreviousData: true,
  });
};

// 🔹 HOOK: Buscar pedido por ID
export const usePedido = (id) => {
  return useQuery({
    queryKey: pedidoKeys.detail(id),
    queryFn: () => fetchPedido(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

// 🔹 HOOK: Buscar pedido por número
export const usePedidoPorNumero = (numero) => {
  return useQuery({
    queryKey: [...pedidoKeys.details(), 'numero', numero],
    queryFn: () => fetchPedidoPorNumero(numero),
    enabled: !!numero,
    staleTime: 5 * 60 * 1000,
  });
};

// 🔹 HOOK: Pedidos por cliente
export const usePedidosPorCliente = (clienteId, params = {}) => {
  return useQuery({
    queryKey: [...pedidoKeys.cliente(clienteId), params],
    queryFn: () => fetchPedidosPorCliente(clienteId, params),
    enabled: !!clienteId,
    staleTime: 5 * 60 * 1000,
  });
};

// 🔹 HOOK: Estatísticas de pedidos
export const useEstatisticasPedidos = (params = {}) => {
  return useQuery({
    queryKey: [...pedidoKeys.statistics(), params],
    queryFn: () => fetchEstatisticasPedidos(params),
    staleTime: 10 * 60 * 1000, // 10 minutos
  });
};

// 🔹 HOOK: Pedidos com pagamentos pendentes
export const usePedidosPagamentosPendentes = () => {
  return useQuery({
    queryKey: pedidoKeys.pendentes(),
    queryFn: fetchPedidosPagamentosPendentes,
    staleTime: 2 * 60 * 1000, // 2 minutos
  });
};

// 🔹 HOOK: Fluxo completo do pedido
export const useFluxoCompletoPedido = (id) => {
  return useQuery({
    queryKey: [...pedidoKeys.detail(id), 'fluxo-completo'],
    queryFn: () => fetchFluxoCompletoPedido(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

// ======================================================
// 🎯 MUTATION HOOKS
// ======================================================

// 🔹 HOOK: Criar pedido
export const useCreatePedido = () => {
  const queryClient = useQueryClient();
  const { setAlert } = useAlert();

  return useMutation({
    mutationFn: createPedido,
    onSuccess: (data) => {
      queryClient.invalidateQueries(pedidoKeys.lists());
      setAlert('Pedido criado com sucesso!', 'success');
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.error || 'Erro ao criar pedido';
      setAlert(errorMessage, 'error');
    },
  });
};

// 🔹 HOOK: Atualizar pedido
export const useUpdatePedido = () => {
  const queryClient = useQueryClient();
  const { setAlert } = useAlert();

  return useMutation({
    mutationFn: updatePedido,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(pedidoKeys.lists());
      queryClient.invalidateQueries(pedidoKeys.detail(variables.id));
      setAlert('Pedido atualizado com sucesso!', 'success');
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.error || 'Erro ao atualizar pedido';
      setAlert(errorMessage, 'error');
    },
  });
};

// 🔹 HOOK: Excluir pedido
export const useDeletePedido = () => {
  const queryClient = useQueryClient();
  const { setAlert } = useAlert();

  return useMutation({
    mutationFn: deletePedido,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(pedidoKeys.lists());
      setAlert('Pedido excluído com sucesso!', 'success');
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.error || 'Erro ao excluir pedido';
      setAlert(errorMessage, 'error');
    },
  });
};

// 🔹 HOOK: Cancelar pedido
export const useCancelarPedido = () => {
  const queryClient = useQueryClient();
  const { setAlert } = useAlert();

  return useMutation({
    mutationFn: cancelarPedido,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(pedidoKeys.lists());
      queryClient.invalidateQueries(pedidoKeys.detail(variables.id));
      setAlert('Pedido cancelado com sucesso!', 'success');
    },
    onError: (error) => {
  	  const errorMessage = error.response?.data?.error || 'Erro ao cancelar pedido';
      setAlert(errorMessage, 'error');
    },
  });
};

// 🔹 HOOK: Processar pedido
export const useProcessarPedido = () => {
  const queryClient = useQueryClient();
  const { setAlert } = useAlert();

  return useMutation({
    mutationFn: processarPedido,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(pedidoKeys.lists());
      queryClient.invalidateQueries(pedidoKeys.detail(variables));
      setAlert('Pedido processado com sucesso!', 'success');
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.error || 'Erro ao processar pedido';
      setAlert(errorMessage, 'error');
    },
  });
};

// 🔹 HOOK: Criar venda a partir do pedido
export const useCriarVendaPedido = () => {
  const queryClient = useQueryClient();
  const { setAlert } = useAlert();

  return useMutation({
  	mutationFn: criarVendaPedido,
  	onSuccess: (data, variables) => {
  	  queryClient.invalidateQueries(pedidoKeys.lists());
  	  queryClient.invalidateQueries(pedidoKeys.detail(variables));
  	  setAlert('Venda criada a partir do pedido com sucesso!', 'success');
  	},
  	onError: (error) => {
  	  const errorMessage = error.response?.data?.error || 'Erro ao criar venda do pedido';
  	  setAlert(errorMessage, 'error');
  	},
  });
};

// 🔹 HOOK: Adicionar pagamento ao pedido
export const useAdicionarPagamento = () => {
  const queryClient = useQueryClient();
  const { setAlert } = useAlert();

  return useMutation({
  	mutationFn: adicionarPagamento,
  	onSuccess: (data, variables) => {
  	  queryClient.invalidateQueries(pedidoKeys.lists());
  	  queryClient.invalidateQueries(pedidoKeys.detail(variables.pedidoId));
  	  setAlert('Pagamento adicionado ao pedido com sucesso!', 'success');
  	},
  	onError: (error) => {
  	  const errorMessage = error.response?.data?.error || 'Erro ao adicionar pagamento';
  	  setAlert(errorMessage, 'error');
  	},
  });
};

// ======================================================
// 🎯 HOOK COMPOSTO PARA MUTAÇÕES
// ======================================================

export const useMutatePedidos = () => {
  const createPedido = useCreatePedido();
  const updatePedido = useUpdatePedido();
  const deletePedido = useDeletePedido();
  const cancelarPedido = useCancelarPedido();
  const processarPedido = useProcessarPedido();
  const criarVendaPedido = useCriarVendaPedido();
  const adicionarPagamento = useAdicionarPagamento();

  return {
  	createPedido,
  	updatePedido,
  	deletePedido,
  	cancelarPedido,
  	processarPedido,
  	criarVendaPedido,
  	adicionarPagamento,
  };
};

// ======================================================
// 🎯 HOOK PARA ESTATÍSTICAS BÁSICAS
// ======================================================

export const usePedidosEstatisticasBasicas = (periodo = 'hoje') => {
  const { data: estatisticasResponse, isLoading } = useEstatisticasPedidos({ periodo });

  // Backend retorna: { success, data: { porStatus: [...], financeiras: { totalReceita, totalPendente, totalPedidos } } }
  const porStatus   = estatisticasResponse?.data?.porStatus  || [];
  const financeiras = estatisticasResponse?.data?.financeiras || {};

  const dadosBasicos = {
    totalPedidos: 0,
    pedidosPendentes: 0,
    pedidosConfirmados: 0,
    pedidosEnviados: 0,
    pedidosEntregues: 0,
    faturamentoTotal: 0,
    ticketMedio: 0,
    taxaConversao: 0,
  };

  if (Array.isArray(porStatus) && porStatus.length > 0) {
    // porStatus items: { _id: 'pendente', count: N, totalValor: N }
    dadosBasicos.totalPedidos      = financeiras.totalPedidos ?? porStatus.reduce((acc, item) => acc + (item.count || 0), 0);
    dadosBasicos.pedidosPendentes  = porStatus.find(item => item._id === 'pendente')?.count   || 0;
    dadosBasicos.pedidosConfirmados = porStatus.find(item => item._id === 'confirmado')?.count || 0;
    dadosBasicos.pedidosEnviados   = porStatus.find(item => item._id === 'enviado')?.count    || 0;
    dadosBasicos.pedidosEntregues  = porStatus.find(item => item._id === 'entregue')?.count   || 0;
    dadosBasicos.faturamentoTotal  = financeiras.totalReceita ?? porStatus.reduce((acc, item) => acc + (item.totalValor || 0), 0);
    dadosBasicos.ticketMedio = dadosBasicos.totalPedidos > 0
      ? dadosBasicos.faturamentoTotal / dadosBasicos.totalPedidos
      : 0;
  } else if (financeiras.totalPedidos != null) {
    dadosBasicos.totalPedidos     = financeiras.totalPedidos;
    dadosBasicos.faturamentoTotal = financeiras.totalReceita || 0;
    dadosBasicos.pedidosPendentes = financeiras.totalPendente || 0;
  }

  return {
    data: dadosBasicos,
    isLoading,
  };
};
